import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { 
  Rocket, 
  ArrowRight,
  Sparkles,
  GitBranch,
  Target
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Phase2TransitionProps {
  sessionId: string;
  problemStatement: string;
  tensions: string[];
  primaryHypothesis?: string;
  decisionQuestion?: string;
  onError: (message: string) => void;
}

const phase2Preview = [
  { icon: GitBranch, title: "Drei strategische Optionen", description: "Verschiedene Wege zur Lösung" },
  { icon: Sparkles, title: "KI-gestützte Analyse", description: "Risiken, Chancen & Fit" },
  { icon: Target, title: "Klare Empfehlung", description: "Datenbasierte Entscheidungshilfe" },
];

export const Phase2Transition = ({
  sessionId,
  problemStatement,
  tensions,
  primaryHypothesis,
  decisionQuestion,
  onError,
}: Phase2TransitionProps) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<"preparing" | "generating" | "ready" | "error">("preparing");
  const [statusMessage, setStatusMessage] = useState("Analysiere deinen Entscheidungsrahmen...");

  useEffect(() => {
    startPhase2Generation();
  }, [sessionId]);

  // Smooth progress animation
  useEffect(() => {
    if (status === "preparing") {
      const timer = setTimeout(() => setProgress(15), 300);
      return () => clearTimeout(timer);
    }
    if (status === "generating") {
      // Simulate progress during generation
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 85) return prev;
          return prev + Math.random() * 8;
        });
      }, 800);
      return () => clearInterval(interval);
    }
    if (status === "ready") {
      setProgress(100);
    }
  }, [status]);

  const startPhase2Generation = async () => {
    try {
      setStatus("preparing");
      setStatusMessage("Analysiere deinen Entscheidungsrahmen...");
      
      // Small delay for UX
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setStatus("generating");
      setStatusMessage("Generiere strategische Optionen mit KI...");
      setProgress(25);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Nicht authentifiziert");
      }

      const response = await supabase.functions.invoke("phase2-start", {
        body: { session_id: sessionId },
      });

      if (response.error) {
        throw new Error(response.error.message || "Fehler beim Generieren");
      }

      setProgress(95);
      setStatusMessage("Optionen bereit!");
      
      // Short delay to show completion
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setStatus("ready");
      setProgress(100);

    } catch (error: any) {
      console.error("Phase 2 generation error:", error);
      setStatus("error");
      onError(error.message || "Fehler beim Starten von Phase 2");
    }
  };

  const handleContinue = () => {
    navigate(`/app/session/${sessionId}/phase2`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-background flex items-center justify-center overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div 
          className="absolute top-0 left-0 w-full h-full"
          style={{
            backgroundImage: `
              radial-gradient(ellipse 80% 50% at 20% 20%, hsl(var(--primary) / 0.08) 0%, transparent 50%),
              radial-gradient(ellipse 60% 60% at 80% 80%, hsl(var(--primary) / 0.06) 0%, transparent 50%),
              radial-gradient(ellipse 40% 40% at 50% 50%, hsl(var(--accent) / 0.04) 0%, transparent 50%)
            `,
          }}
        />
        {/* Floating particles */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 rounded-full bg-primary/20 animate-pulse" />
        <div className="absolute top-1/3 right-1/4 w-3 h-3 rounded-full bg-primary/15 animate-pulse [animation-delay:500ms]" />
        <div className="absolute bottom-1/3 left-1/3 w-2 h-2 rounded-full bg-accent/20 animate-pulse [animation-delay:1000ms]" />
      </div>

      <div className="relative z-10 w-full max-w-2xl mx-auto px-6">
        {/* Main content card */}
        <div className="bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-8 md:p-10 shadow-2xl">
          
          {/* Header with icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 mb-4 animate-scale-in">
              <Rocket className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2 animate-fade-in">
              {status === "ready" ? "Bereit für Phase 2!" : "Phase 1 abgeschlossen"}
            </h1>
            <p className="text-muted-foreground animate-fade-in [animation-delay:100ms]">
              {status === "ready" 
                ? "Deine strategischen Optionen wurden generiert."
                : "Dein Entscheidungsrahmen steht. Wir bereiten jetzt Phase 2 vor."
              }
            </p>
          </div>


          {/* Decision summary - nur Kernhypothese, kein Problem-Statement */}
          {primaryHypothesis && (
            <div className="mb-8 p-4 bg-muted/20 border border-border/30 rounded-lg animate-fade-in [animation-delay:500ms]">
              <p className="text-xs font-medium text-muted-foreground mb-2">Kernhypothese</p>
              <p className="text-sm text-foreground line-clamp-2">
                {primaryHypothesis}
              </p>
            </div>
          )}

          {/* Progress section */}
          {status !== "ready" && status !== "error" && (
            <div className="mb-8 animate-fade-in [animation-delay:600ms]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-foreground">{statusMessage}</p>
                <span className="text-xs text-muted-foreground">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
              
              {/* What's being prepared */}
              <div className="mt-6 space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Was in Phase 2 auf dich wartet
                </p>
                {phase2Preview.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div 
                      key={index}
                      className="flex items-center gap-3 text-sm animate-fade-in"
                      style={{ animationDelay: `${700 + index * 100}ms` }}
                    >
                      <div className="w-6 h-6 rounded-md bg-accent/10 flex items-center justify-center">
                        <Icon className="w-3.5 h-3.5 text-accent" />
                      </div>
                      <div>
                        <span className="font-medium text-foreground">{item.title}</span>
                        <span className="text-muted-foreground"> — {item.description}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Ready state */}
          {status === "ready" && (
            <div className="space-y-6 animate-fade-in">
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      3 strategische Optionen generiert
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Risikoarm · Wertfokus · Skalierbar — drei völlig unterschiedliche Wege, deine Entscheidung zu lösen.
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleContinue} 
                className="w-full gap-2 h-12 text-base"
                size="lg"
              >
                Optionen erkunden
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Error state */}
          {status === "error" && (
            <div className="text-center animate-fade-in">
              <p className="text-sm text-destructive mb-4">
                Es gab ein Problem beim Generieren der Optionen.
              </p>
              <Button onClick={startPhase2Generation} variant="outline">
                Erneut versuchen
              </Button>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <p className="text-center text-xs text-muted-foreground mt-6 animate-fade-in [animation-delay:800ms]">
          Die KI analysiert deinen Entscheidungsrahmen und generiert maßgeschneiderte Optionen.
        </p>
      </div>
    </div>
  );
};